import { useMutation, useQueryClient } from "@tanstack/vue-query";

import { InternalMutationKeys } from "@/data/api/kili/internal/internal-query-keys";
import KiliApi from "@/data/api/kili/kili-api";
import { TActivateProgramRequest } from "@/data/api/kili/programs/program.type";
import { SitesQueryKeys } from "@/data/api/kili/sites/sites-query-keys";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { makeCacheableData } from "@/utils/object";

export interface IActivateProgramCommand extends TActivateProgramRequest {
  siteId: V4HSiteId;
  programId: ProgramId;
  name: string;
}

export function useActivateSiteProgramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: InternalMutationKeys.activateSiteProgram(),
    mutationFn: (body: IActivateProgramCommand) => {
      return KiliApi.activateProgram({ isActive: body.isActive }, { params: { programId: body.programId } });
    },
    onMutate: async (body) => {
      const queryKey = SitesQueryKeys.programs(body.siteId);

      queryClient.setQueryData<SiteProgramOverview[]>(queryKey, (oldValue) => {
        return oldValue?.map((program) => {
          if (body.isActive && program.id === body.programId) {
            return makeCacheableData({ ...program, isActive: true });
          } else {
            return makeCacheableData({ ...program, isActive: false });
          }
        });
      });
    },
    onSettled: (_data, _error, body) => {
      queryClient.invalidateQueries({
        queryKey: SitesQueryKeys.programs(body.siteId),
      });
    },
  });
}
